import React, { useState, useEffect } from 'react';
import { formatDate } from '../lib/utils';
import { toast } from 'sonner';
import { 
  History, 
  Search,
  Filter,
  Download,
  User,
  FileText,
  Briefcase,
  Clock,
  ChevronDown
} from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Badge } from '../components/ui/badge';
import { PageLoader } from '../components/common/LoadingSpinner';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import api from '../lib/api';

const ACTION_COLORS = {
  create: 'bg-green-100 text-green-700',
  update: 'bg-blue-100 text-blue-700',
  delete: 'bg-red-100 text-red-700',
  view: 'bg-slate-100 text-slate-700',
  login: 'bg-purple-100 text-purple-700',
  logout: 'bg-slate-100 text-slate-700',
  export: 'bg-amber-100 text-amber-700',
};

const RESOURCE_ICONS = {
  client: User,
  case: Briefcase,
  document: FileText,
  invoice: FileText,
  time_entry: Clock,
  default: History,
};

export const AuditLogs = () => {
  const [logs, setLogs] = useState([]);
  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [resourceFilter, setResourceFilter] = useState('all');
  const [actionFilter, setActionFilter] = useState('all');
  const [expandedLog, setExpandedLog] = useState(null);

  useEffect(() => {
    fetchData();
  }, [resourceFilter, actionFilter]);

  const fetchData = async () => {
    try {
      const params = {};
      if (resourceFilter !== 'all') params.resource_type = resourceFilter;
      if (actionFilter !== 'all') params.action = actionFilter;
      
      const [logsRes, summaryRes] = await Promise.all([
        api.get('/audit-logs', { params }),
        api.get('/audit-logs/summary'),
      ]);
      setLogs(logsRes.data);
      setSummary(summaryRes.data);
    } catch (error) {
      console.error('Failed to fetch audit logs:', error);
      if (error.response?.status === 403) {
        toast.error('Access denied. Partner role or higher required.');
      }
    } finally {
      setLoading(false);
    }
  };

  const filteredLogs = logs.filter(log => 
    log.user_email?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    log.resource_name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    log.resource_type?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const getResourceIcon = (resourceType) => {
    const Icon = RESOURCE_ICONS[resourceType] || RESOURCE_ICONS.default;
    return <Icon className="h-4 w-4" />;
  };

  if (loading) {
    return <PageLoader text="Loading audit logs..." />;
  }

  return (
    <div className="space-y-6 animate-fade-in" data-testid="audit-logs-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-slate-900 font-['Plus_Jakarta_Sans']">Audit Logs</h1>
          <p className="text-slate-500 mt-1">Track all user actions and system events</p>
        </div>
        <Button variant="outline" className="gap-2">
          <Download className="h-4 w-4" />
          Export Logs
        </Button>
      </div>

      {/* Summary Cards */}
      {summary && (
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <Card>
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <div className="p-2 bg-blue-100 rounded-lg">
                  <History className="h-5 w-5 text-blue-600" />
                </div>
                <div>
                  <p className="text-sm text-slate-500">Total Events</p>
                  <p className="text-xl font-bold">{summary.total_logs?.toLocaleString() || 0}</p>
                </div>
              </div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="p-4">
              <p className="text-sm text-slate-500 mb-2">Top Actions</p>
              <div className="flex flex-wrap gap-2">
                {summary.summary?.slice(0, 3).map((item, i) => (
                  <Badge key={i} variant="outline">
                    {item._id.action}: {item.count}
                  </Badge>
                ))}
              </div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="p-4">
              <p className="text-sm text-slate-500 mb-2">Recent Activity</p>
              <p className="text-sm">
                {summary.recent_activity?.length || 0} events in last 24 hours
              </p>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Filters */}
      <div className="flex flex-col sm:flex-row gap-4">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
          <Input
            placeholder="Search by user or resource..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="pl-10"
          />
        </div>
        <Select value={resourceFilter} onValueChange={setResourceFilter}>
          <SelectTrigger className="w-[180px]">
            <SelectValue placeholder="Resource Type" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Resources</SelectItem>
            <SelectItem value="client">Clients</SelectItem>
            <SelectItem value="case">Cases</SelectItem>
            <SelectItem value="document">Documents</SelectItem>
            <SelectItem value="invoice">Invoices</SelectItem>
            <SelectItem value="user">Users</SelectItem>
          </SelectContent>
        </Select>
        <Select value={actionFilter} onValueChange={setActionFilter}>
          <SelectTrigger className="w-[150px]">
            <SelectValue placeholder="Action" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Actions</SelectItem>
            <SelectItem value="create">Create</SelectItem>
            <SelectItem value="update">Update</SelectItem>
            <SelectItem value="delete">Delete</SelectItem>
            <SelectItem value="view">View</SelectItem>
            <SelectItem value="login">Login</SelectItem>
          </SelectContent>
        </Select>
      </div>

      {/* Logs List */}
      <Card>
        <CardContent className="p-0">
          <div className="divide-y">
            {filteredLogs.map((log) => (
              <div 
                key={log.log_id} 
                className="p-4 hover:bg-slate-50 cursor-pointer"
                onClick={() => setExpandedLog(expandedLog === log.log_id ? null : log.log_id)}
              >
                <div className="flex items-start justify-between">
                  <div className="flex items-start gap-3">
                    <div className="p-2 bg-slate-100 rounded-lg">
                      {getResourceIcon(log.resource_type)}
                    </div>
                    <div>
                      <div className="flex items-center gap-2">
                        <Badge className={ACTION_COLORS[log.action] || ACTION_COLORS.view}>
                          {log.action}
                        </Badge>
                        <span className="text-sm font-medium">{log.resource_type}</span>
                        {log.resource_name && (
                          <span className="text-sm text-slate-500">- {log.resource_name}</span>
                        )}
                      </div>
                      <p className="text-sm text-slate-500 mt-1">
                        by {log.user_email}
                      </p>
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    <span className="text-xs text-slate-400">
                      {formatDate(log.timestamp)}
                    </span>
                    <ChevronDown className={`h-4 w-4 text-slate-400 transition-transform ${expandedLog === log.log_id ? 'rotate-180' : ''}`} />
                  </div>
                </div>
                
                {expandedLog === log.log_id && (
                  <div className="mt-4 p-4 bg-slate-50 rounded-lg text-sm">
                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <p className="text-slate-500">Resource ID</p>
                        <p className="font-mono">{log.resource_id || '-'}</p>
                      </div>
                      <div>
                        <p className="text-slate-500">IP Address</p>
                        <p className="font-mono">{log.ip_address || '-'}</p>
                      </div>
                      {log.details && (
                        <div className="col-span-2">
                          <p className="text-slate-500">Details</p>
                          <pre className="bg-white p-2 rounded mt-1 text-xs overflow-x-auto">
                            {JSON.stringify(log.details, null, 2)}
                          </pre>
                        </div>
                      )}
                    </div>
                  </div>
                )}
              </div>
            ))}
            {filteredLogs.length === 0 && (
              <div className="p-8 text-center text-slate-500">
                <History className="h-12 w-12 mx-auto text-slate-300 mb-3" />
                <p>No audit logs found</p>
              </div>
            )}
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default AuditLogs;
